import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    DollarSign, Calculator, ChevronDown, ChevronUp,
    TrendingUp, TrendingDown, Minus, Equal, RefreshCw, FileText, Receipt, Loader2
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import ARChargesTable from './ARChargesTable';
import APChargesTable from './APChargesTable';
import { useLanguage } from '../../../../context/LanguageContext';
import { baseURL } from '@/config/APIConfig';
import {
    ACCOUNTING_DIVISION,
    buildARSyncBody,
    buildAPSyncBody,
    buildBookingAccountingStatusUrl,
    filterARChargesForSync,
    filterAPChargesForSync,
    mapAccountingStatusOntoCharges,
    findChargeSyncUpdate,
} from "@/utils/accountingBookingUtils";

const API_URL = baseURL.AiBaseURL;

const ARAPChargesContainer = ({
    bookingId,
    bookingNo,
    arCharges,
    setArCharges,
    apCharges,
    setApCharges,
    currencyList = [],
    basisList = [],
    containerList = [],
    chargeCodeList = [],
    customerId,
    customerName,
    companyId,
    division = ACCOUNTING_DIVISION.IMPORT,
    vendorId,
    vendorName,
}) => {
    const { t } = useLanguage();
    const navigate = useNavigate();
    const [viewMode, setViewMode] = useState('both'); // 'ar', 'ap', 'both'
    const [isExpanded, setIsExpanded] = useState(true);
    const [isSyncingAR, setIsSyncingAR] = useState(false);
    const [isSyncingAP, setIsSyncingAP] = useState(false);
    const [accountingStatus, setAccountingStatus] = useState(null);

    // Calculate totals
    const totalAR = arCharges.reduce((sum, c) => {
        const amount = parseFloat(c.AMOUNT) || 0;
        const qty = parseFloat(c.QUANTITY) || 1;
        return sum + (amount * qty);
    }, 0);

    const totalAP = apCharges.reduce((sum, c) => {
        const amount = parseFloat(c.AMOUNT) || 0;
        const qty = parseFloat(c.QUANTITY) || 1;
        return sum + (amount * qty);
    }, 0);

    const profit = totalAR - totalAP;
    const margin = totalAR > 0 ? ((profit / totalAR) * 100) : 0;

    // Fetch accounting status for this booking
    const fetchAccountingStatus = useCallback(async () => {
        if (!bookingId || bookingId === 0 || !companyId) return;

        try {
            const url = buildBookingAccountingStatusUrl(
                API_URL,
                bookingId,
                companyId,
                division
            );
            const response = await fetch(url);
            if (response.ok) {
                const data = await response.json();
                setAccountingStatus(data);
                setArCharges((prev) =>
                    mapAccountingStatusOntoCharges(prev, [], data).arCharges
                );
                setApCharges((prev) =>
                    mapAccountingStatusOntoCharges([], prev, data).apCharges
                );
                console.log("=========arCharges=========== :", arCharges);
                console.log("=========apCharges=========== :", apCharges);
            }
        } catch (error) {
            console.error('Error fetching accounting status:', error);
        }
    }, [bookingId, companyId, division, setArCharges, setApCharges]);

    useEffect(() => {
        // console.log('Fetching accounting status for booking ID:', bookingId);
        fetchAccountingStatus();
    }, [fetchAccountingStatus]);

    const handleJobCosting = () => {
        if (bookingId && bookingId !== 0) {
            navigate(`/job-costing/${bookingId}?mode=import`);
        } else {
            toast.error('Please save the booking first to access Job Costing.');
        }
    };

    // Sync AR charges to Customer Invoices
    const handleSyncARToInvoice = async () => {
        if (!bookingId || bookingId === 0) {
            toast.error('Please save the booking first before syncing to invoices.');
            return;
        }

        // Filter charges that have a Bill To Party
        const chargesWithParty = filterARChargesForSync(arCharges);

        if (chargesWithParty.length === 0) {
            toast.warning('No AR charges with Bill To Party to sync. Please assign a Bill To Party to at least one charge.');
            return;
        }

        setIsSyncingAR(true);
        try {
            const payload = buildARSyncBody({
                bookingId,
                bookingNo,
                charges: chargesWithParty,
                customerId,
                customerName,
                companyId,
                division,
            });
            const response = await fetch(`${API_URL}/api/accounting/sync/ar-to-invoice`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload),
            });

            const result = await response.json();

            if (response.ok) {
                toast.success(result.message);

                // Update charge statuses
                if (result.updated_charges) {
                    setArCharges(prev => prev.map(charge => {
                        const update = findChargeSyncUpdate(result.updated_charges, charge);
                        if (update) {
                            return {
                                ...charge,
                                invoiceId: update.invoice_id,
                                invoiceNo: update.invoice_no,
                                invoiceStatus: update.invoice_status,
                                INVOICE_ID: update.invoice_id,
                                INVOICE_NO: update.invoice_no,
                                INVOICE_STATUS: update.invoice_status,
                            };
                        }
                        return charge;
                    }));
                }

                // Refresh accounting status
                await fetchAccountingStatus();
            } else {
                toast.error(result.detail || 'Failed to sync AR charges');
            }
        } catch (error) {
            console.error('Error syncing AR to invoice:', error);
            toast.error('Failed to sync AR charges to invoices');
        } finally {
            setIsSyncingAR(false);
        }
    };

    // Sync AP charges to Vendor Bills
    const handleSyncAPToBill = async () => {
        if (!bookingId || bookingId === 0) {
            toast.error('Please save the booking first before syncing to bills.');
            return;
        }

        // Filter charges that have a Vendor
        const chargesWithVendor = filterAPChargesForSync(apCharges);

        if (chargesWithVendor.length === 0) {
            toast.warning('No AP charges with Vendor to sync. Please assign a Vendor to at least one charge.');
            return;
        }

        setIsSyncingAP(true);
        try {
            const payload = buildAPSyncBody({
                bookingId,
                bookingNo,
                charges: chargesWithVendor,
                vendorId,
                vendorName,
                companyId,
                division,
            });
            const response = await fetch(`${API_URL}/api/accounting/sync/ap-to-bill`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload),
            });

            const result = await response.json();

            if (response.ok) {
                toast.success(result.message);

                // Update charge statuses
                if (result.updated_charges) {
                    setApCharges(prev => prev.map(charge => {
                        const update = findChargeSyncUpdate(result.updated_charges, charge);
                        if (update) {
                            return {
                                ...charge,
                                billId: update.bill_id,
                                billNo: update.bill_no,
                                billStatus: update.bill_status,
                                BILL_ID: update.bill_id,
                                BILL_NO: update.bill_no,
                                BILL_STATUS: update.bill_status,
                            };
                        }
                        return charge;
                    }));
                }

                // Refresh accounting status
                await fetchAccountingStatus();
            } else {
                toast.error(result.detail || 'Failed to sync AP charges');
            }
        } catch (error) {
            console.error('Error syncing AP to bill:', error);
            toast.error('Failed to sync AP charges to bills');
        } finally {
            setIsSyncingAP(false);
        }
    };

    return (
        <div className="bg-white dark:bg-slate-900 rounded-xl shadow-lg border border-slate-200 dark:border-slate-800">
            {/* Header */}
            <div
                className="flex items-center justify-between px-4 py-3 bg-gradient-to-r from-slate-100 to-slate-50 dark:from-slate-800 dark:to-slate-900 border-b border-slate-200 dark:border-slate-700 cursor-pointer"
                onClick={() => setIsExpanded(!isExpanded)}
                data-testid="arap-charges-header"
            >
                <div className="flex items-center gap-3">
                    <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
                        <DollarSign className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                    </div>
                    <div>
                        <h2 className="text-sm font-semibold text-slate-800 dark:text-slate-200">
                            {t('arApInformation')}
                        </h2>
                        <p className="text-xs text-slate-500 dark:text-slate-400">
                            {t('manageRevenueAndCost')}
                        </p>
                    </div>
                </div>
                <div className="flex items-center gap-2">
                    {/* View Toggle */}
                    <div className="flex items-center bg-slate-200 dark:bg-slate-700 rounded-lg p-0.5" onClick={(e) => e.stopPropagation()}>
                        <button
                            onClick={() => setViewMode('ar')}
                            className={`px-3 py-1.5 text-xs font-medium rounded-md transition-all ${viewMode === 'ar'
                                ? 'bg-amber-500 text-white shadow-sm'
                                : 'text-slate-600 dark:text-slate-400 hover:bg-slate-300 dark:hover:bg-slate-600'
                                }`}
                            data-testid="view-mode-ar"
                        >
                            AR
                        </button>
                        <button
                            onClick={() => setViewMode('ap')}
                            className={`px-3 py-1.5 text-xs font-medium rounded-md transition-all ${viewMode === 'ap'
                                ? 'bg-green-500 text-white shadow-sm'
                                : 'text-slate-600 dark:text-slate-400 hover:bg-slate-300 dark:hover:bg-slate-600'
                                }`}
                            data-testid="view-mode-ap"
                        >
                            AP
                        </button>
                        <button
                            onClick={() => setViewMode('both')}
                            className={`px-3 py-1.5 text-xs font-medium rounded-md transition-all ${viewMode === 'both'
                                ? 'bg-blue-500 text-white shadow-sm'
                                : 'text-slate-600 dark:text-slate-400 hover:bg-slate-300 dark:hover:bg-slate-600'
                                }`}
                            data-testid="view-mode-both"
                        >
                            Both
                        </button>
                    </div>

                    {/* Job Costing Button */}
                    <Button
                        onClick={(e) => {
                            e.stopPropagation();
                            handleJobCosting();
                        }}
                        className="bg-emerald-600 hover:bg-emerald-700 text-white text-xs"
                        size="sm"
                        data-testid="job-costing-btn"
                    >
                        <Calculator className="w-4 h-4 mr-1" />
                        {t('jobCosting')}
                    </Button>

                    {/* Expand/Collapse */}
                    <button className="p-1 hover:bg-slate-200 dark:hover:bg-slate-700 rounded" data-testid="toggle-expand">
                        {isExpanded ? (
                            <ChevronUp className="w-5 h-5 text-slate-500" />
                        ) : (
                            <ChevronDown className="w-5 h-5 text-slate-500" />
                        )}
                    </button>
                </div>
            </div>

            {/* Content */}
            <AnimatePresence>
                {isExpanded && (
                    <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.2 }}
                        className="p-4 space-y-6"
                    >

                        {/* Accounting Status Summary */}
                        {accountingStatus && (accountingStatus?.invoices?.length > 0 || accountingStatus.bills?.length > 0) && (
                            <div className="bg-slate-50 dark:bg-slate-800/50 p-3 rounded-lg border border-slate-200 dark:border-slate-700">
                                <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-4 text-xs">
                                        {accountingStatus.invoices?.length > 0 && (
                                            <div className="flex items-center gap-2">
                                                <Receipt className="w-4 h-4 text-amber-500" />
                                                <span className="text-slate-600 dark:text-slate-400">
                                                    {accountingStatus.invoices.length} Invoice(s) -
                                                    <span className="font-semibold text-amber-600 ml-1">
                                                        ${accountingStatus.total_ar?.toLocaleString('en-US', { minimumFractionDigits: 2 }) || '0.00'}
                                                    </span>
                                                    {accountingStatus.ar_collected > 0 && (
                                                        <span className="text-green-600 ml-1">
                                                            (${accountingStatus.ar_collected.toLocaleString('en-US', { minimumFractionDigits: 2 })} collected)
                                                        </span>
                                                    )}
                                                </span>
                                            </div>
                                        )}
                                        {accountingStatus.bills?.length > 0 && (
                                            <div className="flex items-center gap-2">
                                                <FileText className="w-4 h-4 text-green-500" />
                                                <span className="text-slate-600 dark:text-slate-400">
                                                    {accountingStatus.bills.length} Bill(s) -
                                                    <span className="font-semibold text-green-600 ml-1">
                                                        ${accountingStatus.total_ap?.toLocaleString('en-US', { minimumFractionDigits: 2 }) || '0.00'}
                                                    </span>
                                                    {accountingStatus.ap_paid > 0 && (
                                                        <span className="text-blue-600 ml-1">
                                                            (${accountingStatus.ap_paid.toLocaleString('en-US', { minimumFractionDigits: 2 })} paid)
                                                        </span>
                                                    )}
                                                </span>
                                            </div>
                                        )}
                                    </div>
                                    <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={fetchAccountingStatus}
                                        className="text-xs"
                                        data-testid="refresh-status-btn"
                                    >
                                        <RefreshCw className="w-3 h-3 mr-1" />
                                        {t('refresh')}
                                    </Button>
                                </div>
                            </div>
                        )}


                        {/* AR Table */}
                        {(viewMode === 'ar' || viewMode === 'both') && (
                            <ARChargesTable
                                charges={arCharges}
                                setCharges={setArCharges}
                                currencyList={currencyList}
                                basisList={basisList}
                                containerList={containerList}
                                chargeCodeList={chargeCodeList}
                                onSyncToInvoice={handleSyncARToInvoice}
                                isSyncing={isSyncingAR}
                                bookingId={bookingId}
                                totalinvoice={accountingStatus?.invoices?.length}
                            />
                        )}

                        {/* AP Table */}
                        {(viewMode === 'ap' || viewMode === 'both') && (
                            <APChargesTable
                                charges={apCharges}
                                setCharges={setApCharges}
                                currencyList={currencyList}
                                basisList={basisList}
                                containerList={containerList}
                                chargeCodeList={chargeCodeList}
                                onSyncToBill={handleSyncAPToBill}
                                isSyncing={isSyncingAP}
                                bookingId={bookingId}
                                totalbill={accountingStatus?.bills?.length}
                            />
                        )}

                        {/* Profit Summary - Only show in Both mode */}
                        {viewMode === 'both' && (
                            <div className="bg-gradient-to-r from-amber-50 via-white to-green-50 dark:from-amber-900/10 dark:via-slate-800 dark:to-green-900/10 p-4 rounded-xl border border-slate-200 dark:border-slate-700">
                                <div className="grid grid-cols-5 gap-4 items-center">
                                    {/* Total AR */}
                                    <div className="text-center">
                                        <div className="flex items-center justify-center gap-2 mb-1">
                                            <TrendingUp className="w-4 h-4 text-amber-500" />
                                            <span className="text-xs font-medium text-slate-500 dark:text-slate-400">{t('totalAR')} (USD)</span>
                                        </div>
                                        <div className="px-4 py-2 bg-amber-100 dark:bg-amber-900/30 rounded-lg">
                                            <span className="text-lg font-bold text-amber-700 dark:text-amber-300" data-testid="total-ar">
                                                ${totalAR.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                            </span>
                                        </div>
                                    </div>

                                    {/* Minus */}
                                    <div className="text-center">
                                        <div className="w-8 h-8 mx-auto bg-slate-200 dark:bg-slate-700 rounded-full flex items-center justify-center">
                                            <Minus className="w-4 h-4 text-slate-500" />
                                        </div>
                                    </div>

                                    {/* Total AP */}
                                    <div className="text-center">
                                        <div className="flex items-center justify-center gap-2 mb-1">
                                            <TrendingDown className="w-4 h-4 text-green-500" />
                                            <span className="text-xs font-medium text-slate-500 dark:text-slate-400">{t('totalAP')} (USD)</span>
                                        </div>
                                        <div className="px-4 py-2 bg-green-100 dark:bg-green-900/30 rounded-lg">
                                            <span className="text-lg font-bold text-green-700 dark:text-green-300" data-testid="total-ap">
                                                ${totalAP.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                            </span>
                                        </div>
                                    </div>

                                    {/* Equals */}
                                    <div className="text-center">
                                        <div className="w-8 h-8 mx-auto bg-slate-200 dark:bg-slate-700 rounded-full flex items-center justify-center">
                                            <Equal className="w-4 h-4 text-slate-500" />
                                        </div>
                                    </div>

                                    {/* Profit */}
                                    <div className="text-center">
                                        <div className="flex items-center justify-center gap-2 mb-1">
                                            <DollarSign className="w-4 h-4 text-blue-500" />
                                            <span className="text-xs font-medium text-slate-500 dark:text-slate-400">{t('profit')}</span>
                                        </div>
                                        <div className={`px-4 py-2 rounded-lg ${profit >= 0
                                            ? 'bg-blue-100 dark:bg-blue-900/30'
                                            : 'bg-red-100 dark:bg-red-900/30'
                                            }`}>
                                            <span className={`text-lg font-bold ${profit >= 0
                                                ? 'text-blue-700 dark:text-blue-300'
                                                : 'text-red-700 dark:text-red-300'
                                                }`} data-testid="profit-amount">
                                                ${profit.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                            </span>
                                            <div className={`text-xs ${profit >= 0
                                                ? 'text-blue-600 dark:text-blue-400'
                                                : 'text-red-600 dark:text-red-400'
                                                }`}>
                                                {margin.toFixed(1)}% {t('margin')}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}


                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
};

export default ARAPChargesContainer;
